'use client';

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { Check, ArrowsDownUp, CaretDown, Funnel } from '@phosphor-icons/react';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { Footer } from '@/components/seasons/Footer';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { FlowFooter } from '@/components/seasons/FlowFooter';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { CometFooter } from '@/components/seasons/CometFooter';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GalaxyFooter } from '@/components/seasons/GalaxyFooter';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { GravityFooter } from '@/components/seasons/GravityFooter';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { StoryhealthFooter } from '@/components/seasons/StoryhealthFooter';
import { SimpleButton } from '@/components/seasons/SimpleButton';
import { useClickOutside } from '@/lib/seasons/useClickOutside';
import { useCtrlPanelPosition } from '@/lib/seasons/useCtrlPanelPosition';
import { whitepaperGradient } from '@/lib/whitepaper-gradient';
import { brandExternalUrl } from '@/lib/brand-urls';

const MotionLink = motion(Link);

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const fadeUp = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4, ease: EASE_OUT } } };
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

const PAGE_SIZE = 12;

// ─── PDF page count hook ──────────────────────────────────────────────────────
// Uses the frontmatter value if present; otherwise fetches from /api/pdf-page-count.
// Result is cached in a module-level map so sibling cards share one request.
const _pageCountCache = new Map<string, number>();

function usePdfPageCount(pdfUrl: string | undefined, initial: number | undefined): number | null {
  const [count, setCount] = useState<number | null>(() => {
    if (initial) return initial;
    if (pdfUrl && _pageCountCache.has(pdfUrl)) return _pageCountCache.get(pdfUrl)!;
    return null;
  });

  useEffect(() => {
    if (!pdfUrl || count !== null) return;
    if (_pageCountCache.has(pdfUrl)) { setCount(_pageCountCache.get(pdfUrl)!); return; }
    let cancelled = false;
    fetch(`/api/pdf-page-count?url=${encodeURIComponent(pdfUrl)}`)
      .then(r => r.ok ? r.json() : null)
      .then((data: { pages: number } | null) => {
        if (!cancelled && data?.pages) {
          _pageCountCache.set(pdfUrl, data.pages);
          setCount(data.pages);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pdfUrl, count]);

  return count;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PostSummary {
  slug: string;
  title: string;
  dek?: string;
  category?: string;
  date?: string;
  heroImg?: string;
  authorName?: string;
  authorCount?: number;
  readMinutes?: number;
  pdfUrl?: string;
  pdfPageCount?: number;
  tags?: string[];        // slug-keys from resource-tags.json (used by filter chips)
  mdxTags?: string[];     // human-readable tags from MDX frontmatter (used by search + detail page)
  brand?: string;
  isFeatured?: boolean;
  hasBody?: boolean;
  newsLink?: string;
}

const VISIBLE_BRANDS = new Set(['innovaccer', 'gravity']);

const BRANDS: { value: string; label: string }[] = [
  { value: 'innovaccer', label: 'Innovaccer' },
  { value: 'gravity',    label: 'Gravity' },
];

interface FilterTag {
  value: string;
  label: string;
}

interface Props {
  posts: PostSummary[];
  filterTags?: FilterTag[];
  initialTab?: Tab;
  /** 'flow' or 'comet' renders brand nav/footer, hides brand filter,
      scopes posts to that brand, and routes under /<brand>/resources. */
  variant?: 'innovaccer' | 'flow' | 'comet' | 'galaxy' | 'gravity' | 'storyhealth';
}

// ─── Resource URL helper ──────────────────────────────────────────────────────

const CATEGORY_URL_SEGMENT: Record<string, string> = {
  'White Paper':     'white-papers',
  'Case Study':      'case-studies',
  'Industry Report': 'industry-reports',
  'Brochure':        'brochures',
};

function resourceHref(slug: string, category?: string, pdfUrl?: string, hasBody?: boolean, brand?: string, newsLink?: string): string {
  const external = brandExternalUrl(brand, slug, category);
  if (external) return external;
  // Use the explicit canonical URL when set (overrides all other logic).
  if (newsLink) return newsLink;
  // Posts with no PDF and no body content are stub pages — link to the live Innovaccer.com page.
  if (!pdfUrl && !hasBody) {
    const seg = category ? CATEGORY_URL_SEGMENT[category] : undefined;
    const path = seg ? `resources/${seg}/${slug}` : `resources/${slug}`;
    return `https://innovaccer.com/${path}`;
  }
  const seg = category ? CATEGORY_URL_SEGMENT[category] : undefined;
  return seg ? `/resources/${seg}/${slug}` : `/resources/${slug}`;
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type Tab = 'All' | 'White Papers' | 'Case Studies' | 'Industry Reports' | 'Brochures';

const TABS: Tab[] = ['All', 'White Papers', 'Case Studies', 'Industry Reports', 'Brochures'];

// Display labels for tabs (shown in UI)
const TAB_DISPLAY: Record<Tab, string> = {
  'All': 'All',
  'White Papers': 'Whitepapers',
  'Case Studies': 'Case Studies',
  'Industry Reports': 'Industry Reports',
  'Brochures': 'Brochures',
};

const TAB_CATEGORY: Record<Tab, string | null> = {
  'All': null,
  'White Papers': 'White Paper',
  'Case Studies': 'Case Study',
  'Industry Reports': 'Industry Report',
  'Brochures': 'Brochure',
};

function resolveInitialTab(tabParam: string | null): Tab {
  if (!tabParam) return 'All';
  // Backward-compat: research papers were merged into industry reports.
  if (tabParam.toLowerCase() === 'research papers') return 'Industry Reports';
  const match = TABS.find(t => t.toLowerCase() === tabParam.toLowerCase());
  return match ?? 'All';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SORT_OPTIONS: { label: string; value: 'newest' | 'oldest' }[] = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
];

function formatDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Visual for a search-result row's media slot. Returns either:
 * - an <img> src string (when a heroImg is available, or as a picsum fallback)
 * - the special value 'gradient' (when only a PDF — caller should render a
 *   gradient div seeded from the slug)
 */
function imageFor(post: PostSummary): string {
  if (post.heroImg) return post.heroImg;
  if (post.pdfUrl) return 'gradient';
  return `https://picsum.photos/seed/res-${post.slug}/800/500`;
}

// Normalise display labels: "White Paper" → "Whitepaper", "White Papers" → "Whitepapers"
function displayCategory(cat: string | undefined): string {
  if (!cat) return '';
  if (cat === 'White Paper') return 'Whitepaper';
  if (cat === 'White Papers') return 'Whitepapers';
  return cat;
}

function MetaSep() { return <span className="bl-sep" aria-hidden="true" />; }

// ─── Brand dropdown ───────────────────────────────────────────────────────────

function BrandDropdown({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const hasActive = selected.length > 0;

  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  }

  return (
    <div className="bl-ctrl" ref={ref}>
      <button ref={triggerRef} type="button" className={`bl-ctrl__trigger${hasActive ? ' bl-ctrl__trigger--active' : ''}`} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        Product
        {hasActive && <span className="bl-ctrl__badge">{selected.length}</span>}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel" role="listbox" aria-multiselectable="true" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          <div className="bl-ctrl__options">
            {BRANDS.map(b => {
              const on = selected.includes(b.value);
              return (
                <button key={b.value} type="button"
                  className={`bl-ctrl__option${on ? ' bl-ctrl__option--selected' : ''}`}
                  onClick={() => toggle(b.value)} role="option" aria-selected={on}>
                  <span className={`bl-ctrl__check${on ? ' bl-ctrl__check--on' : ''}`}>{on && <Check size={10} weight="bold" color="white" />}</span>
                  {b.label}
                </button>
              );
            })}
          </div>
          <div className="bl-ctrl__footer">
            <button type="button" className="bl-ctrl__clear" onClick={() => onChange([])}>Clear all</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sort dropdown ────────────────────────────────────────────────────────────

function SortDropdown({ value, onChange }: { value: 'newest' | 'oldest'; onChange: (v: 'newest' | 'oldest') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const label = SORT_OPTIONS.find(o => o.value === value)?.label ?? 'Sort';
  return (
    <div className="bl-ctrl" ref={ref}>
      <button ref={triggerRef} className="bl-ctrl__trigger" type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <ArrowsDownUp size={13} />
        {label}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel bl-ctrl__panel--right" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          {SORT_OPTIONS.map(o => (
            <button key={o.value} type="button"
              className={`bl-ctrl__option${value === o.value ? ' bl-ctrl__option--selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              <span className="bl-ctrl__check-slot">
                {value === o.value && <Check size={11} weight="bold" />}
              </span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filter dropdown ──────────────────────────────────────────────────────────

function FilterDropdown({ tags, selected, onChange }: {
  tags: FilterTag[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const hasActive = selected.length > 0;

  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  }

  return (
    <div className="bl-ctrl" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className={`bl-ctrl__trigger${hasActive ? ' bl-ctrl__trigger--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <Funnel size={13} />
        Filter
        {hasActive && <span className="bl-ctrl__badge">{selected.length}</span>}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel" role="listbox" aria-multiselectable="true" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          <div className="bl-ctrl__options">
            {tags.map(tag => {
              const on = selected.includes(tag.value);
              return (
                <button
                  key={tag.value}
                  type="button"
                  className={`bl-ctrl__option${on ? ' bl-ctrl__option--selected' : ''}`}
                  onClick={() => toggle(tag.value)}
                  role="option"
                  aria-selected={on}
                >
                  <span className={`bl-ctrl__check${on ? ' bl-ctrl__check--on' : ''}`}>
                    {on && <Check size={10} weight="bold" color="white" />}
                  </span>
                  {tag.label}
                </button>
              );
            })}
          </div>
          {hasActive && (
            <div className="bl-ctrl__footer">
              <button type="button" className="bl-ctrl__clear" onClick={() => onChange([])}>Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────────

function SearchResultRow({ post }: { post: PostSummary }) {
  const pages = usePdfPageCount(post.pdfUrl, post.pdfPageCount);
  const href = resourceHref(post.slug, post.category, post.pdfUrl, post.hasBody, post.brand, post.newsLink);
  const isExternal = href.startsWith('https://');
  return (
    <Link href={href} className="bl-sr-row" {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div className="bl-sr-row__body">
        <div className="bl-sr-row__meta">
          {post.category && <span className="bl-post-card__meta-text">{displayCategory(post.category)}</span>}
          <span className="bl-sep" aria-hidden="true" />
          <span className="bl-post-card__meta-text">{formatDate(post.date || '')}</span>
          {pages ? <><span className="bl-sep" aria-hidden="true" /><span className="bl-post-card__meta-text">{pages} pages</span></> : null}
        </div>
        <h3 className="type-display-sm bl-sr-row__title">{post.title}</h3>
        {post.dek && <p className="bl-sr-row__dek">{post.dek}</p>}
      </div>
      <div
        className="bl-sr-row__media"
        style={imageFor(post) !== 'gradient' ? {
          backgroundImage: `url(${imageFor(post)})`,
        } : undefined}
      >
        {imageFor(post) === 'gradient' ? (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: whitepaperGradient(post.slug) }} />
        ) : (
          <Image fill src={imageFor(post)} alt="" style={{ objectFit: 'contain', objectPosition: 'center center', zIndex: 1 }} sizes="220px" />
        )}
      </div>
    </Link>
  );
}

function ResourceCard({ post }: { post: PostSummary }) {
  const pages = usePdfPageCount(post.pdfUrl, post.pdfPageCount);
  const href = resourceHref(post.slug, post.category, post.pdfUrl, post.hasBody, post.brand, post.newsLink);
  const isExternal = href.startsWith('https://');
  return (
    <MotionLink href={href} className="bl-post-card" variants={fadeUp} {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div className="bl-post-card__media" style={{ background: '#fff' }}>
        {post.heroImg ? (
          <Image fill src={post.heroImg} alt="" style={{ objectFit: 'contain', objectPosition: 'center center' }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
        ) : (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: whitepaperGradient(post.slug) }} />
        )}
      </div>
      <div className="bl-post-card__body">
        <h3 className="type-heading-md bl-post-card__title">{post.title}</h3>
        <div className="bl-post-card__meta">
          {post.category && <><span className="bl-post-card__meta-text">{displayCategory(post.category)}</span><MetaSep /></>}
          <span className="bl-post-card__meta-text">{formatDate(post.date || '')}</span>
          {pages ? <><MetaSep /><span className="bl-post-card__meta-text">{pages} pages</span></> : null}
        </div>
      </div>
    </MotionLink>
  );
}

function StackItem({ post }: { post: PostSummary }) {
  const pages = usePdfPageCount(post.pdfUrl, post.pdfPageCount);
  const href = resourceHref(post.slug, post.category, post.pdfUrl, post.hasBody, post.brand, post.newsLink);
  const isExternal = href.startsWith('https://');
  return (
    <Link href={href} className="bl-post-card" {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div className="bl-post-card__media" style={{ background: '#fff' }}>
        {post.heroImg ? (
          <Image fill src={post.heroImg} alt="" style={{ objectFit: 'contain', objectPosition: 'center center' }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
        ) : (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: whitepaperGradient(post.slug) }} />
        )}
      </div>
      <div className="bl-post-card__body">
        <h3 className="type-heading-md bl-post-card__title">{post.title}</h3>
        <div className="bl-post-card__meta">
          {post.category && <><span className="bl-post-card__meta-text">{displayCategory(post.category)}</span><MetaSep /></>}
          <span className="bl-post-card__meta-text">{formatDate(post.date || '')}</span>
          {pages ? <><MetaSep /><span className="bl-post-card__meta-text">{pages} pages</span></> : null}
        </div>
      </div>
    </Link>
  );
}

function FeaturedCluster({ featured, stack }: { featured: PostSummary; stack: PostSummary[] }) {
  const pages = usePdfPageCount(featured.pdfUrl, featured.pdfPageCount);
  return (
    <div className="bl-efc">
      <Link href={resourceHref(featured.slug, featured.category, featured.pdfUrl, featured.hasBody, featured.brand, featured.newsLink)} className="bl-efc__main" aria-label={featured.title}
        {...(resourceHref(featured.slug, featured.category, featured.pdfUrl, featured.hasBody, featured.brand, featured.newsLink).startsWith('https://') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        <div className="bl-efc__main-media" style={{ background: '#fff' }}>
          {featured.heroImg ? (
            <Image fill src={featured.heroImg} alt="" style={{ objectFit: 'contain', objectPosition: 'center center', zIndex: 1 }} sizes="(max-width: 768px) 100vw, 60vw" priority />
          ) : (
            <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: whitepaperGradient(featured.slug) }} />
          )}
        </div>
        <div className="bl-efc__main-body">
          <h2 className="type-display-md bl-efc__main-title">{featured.title}</h2>
          {featured.dek && <p className="bl-efc__main-dek">{featured.dek}</p>}
          <div className="bl-efc__main-footer">
            {featured.category && <><span className="bl-post-card__meta-text">{displayCategory(featured.category)}</span><MetaSep /></>}
            <span className="bl-post-card__meta-text">{formatDate(featured.date || '')}</span>
            {pages ? <><MetaSep /><span className="bl-post-card__meta-text">{pages} pages</span></> : null}
          </div>
        </div>
      </Link>
      <div className="bl-efc__stack">
        {stack.map(post => <StackItem key={post.slug} post={post} />)}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ResourcesClientPage({ posts, filterTags = [], initialTab, variant = 'innovaccer' }: Props) {
  const isFlow = variant === 'flow';
  const isComet = variant === 'comet';
  const isGalaxy = variant === 'galaxy';
  const isGravity = variant === 'gravity';
  const isStoryhealth = variant === 'storyhealth';
  const isBrand = isFlow || isComet || isGalaxy || isGravity || isStoryhealth;
  const basePath = isFlow ? '/flow/resources' : isComet ? '/comet/resources' : isGalaxy ? '/galaxy/resources' : isGravity ? '/gravity/resources' : isStoryhealth ? '/storyhealth/resources' : '/resources';
  const allowedBrands = isFlow ? new Set(['flow']) : isComet ? new Set(['comet']) : isGalaxy ? new Set(['galaxy']) : isGravity ? new Set(['gravity']) : isStoryhealth ? new Set(['storyhealth']) : VISIBLE_BRANDS;
  const searchParams = useSearchParams();
  const initialBrand = searchParams.get('brand') ?? '';
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) return resolveInitialTab(tabParam);
    return initialTab ?? 'All';
  });
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filterBrand, setFilterBrand] = useState<string[]>(
    initialBrand && BRANDS.some(b => b.value === initialBrand) ? [initialBrand] : []
  );
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') ?? '');

  // Skip the next searchParams sync when the tab change came from user interaction
  // (pushState updates useSearchParams which would otherwise reset activeTab back to initialTab)
  const skipTabSyncRef = useRef(false);

  // Sync when navigating back from a detail page — restores tab, search query, and brand from URL
  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    setSearchQuery(q);
    const brand = searchParams.get('brand') ?? '';
    setFilterBrand(brand ? brand.split(',').filter(v => BRANDS.some(b => b.value === v)) : []);
    if (skipTabSyncRef.current) { skipTabSyncRef.current = false; return; }
    const tabParam = searchParams.get('tab');
    setActiveTab(tabParam ? resolveInitialTab(tabParam) : (initialTab ?? 'All'));
  }, [searchParams, initialTab]);

  // How many posts live under each tab for this brand, ignoring the transient
  // tag/brand/search filters — used only to decide whether a tab is worth
  // showing at all. 'All' always shows even when the whole page is empty.
  const tabCounts = useMemo(() => {
    const brandPosts = posts.filter(p => allowedBrands.has(p.brand || 'innovaccer'));
    const counts: Record<Tab, number> = { All: brandPosts.length, 'White Papers': 0, 'Case Studies': 0, 'Industry Reports': 0, Brochures: 0 };
    for (const p of brandPosts) {
      const tab = TABS.find(t => TAB_CATEGORY[t] === p.category);
      if (tab) counts[tab]++;
    }
    return counts;
  }, [posts, allowedBrands]);
  const visibleTabs = useMemo(() => TABS.filter(t => t === 'All' || tabCounts[t] > 0), [tabCounts]);

  // If the active tab disappears (no posts left under it), fall back to "All"
  // instead of showing a dead, unselectable tab with an empty state.
  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab('All');
  }, [visibleTabs, activeTab]);

  const filtered = useMemo(() => {
    const cat = TAB_CATEGORY[activeTab];
    let list = (cat ? posts.filter(p => p.category === cat) : posts)
      .filter(p => allowedBrands.has(p.brand || 'innovaccer'));
    if (selectedTags.length > 0) {
      list = list.filter(p => (p.tags ?? []).some(t => selectedTags.includes(t)));
    }
    if (filterBrand.length > 0) {
      list = list.filter(p => filterBrand.includes(p.brand || 'innovaccer'));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(p =>
        p.title.toLowerCase().includes(q) ||
        (p.dek || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.tags ?? []).some(t => t.toLowerCase().includes(q)) ||
        (p.mdxTags ?? []).some(t => t.toLowerCase().includes(q)),
      );
    }
    return [...list].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return sortOrder === 'newest' ? db - da : da - db;
    });
  }, [posts, activeTab, sortOrder, selectedTags, filterBrand, searchQuery]);

  // Reset visible count when search or filters change
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [searchQuery, activeTab, selectedTags, filterBrand, sortOrder]);

  function handleTabChange(tab: Tab) {
    skipTabSyncRef.current = true;
    setActiveTab(tab);
    setVisibleCount(PAGE_SIZE);
    // Navigate to the canonical path for the category so the URL stays clean.
    // /resources → All, /resources/white-papers → White Papers tab, etc.
    const TAB_URL_SEGMENT: Record<Tab, string | null> = {
      'All':              null,
      'White Papers':     'white-papers',
      'Case Studies':     'case-studies',
      'Industry Reports': 'industry-reports',
      'Brochures':        'brochures',
    };
    const segment = TAB_URL_SEGMENT[tab];
    const categoryPath = segment ? `${basePath}/${segment}` : basePath;
    const params = new URLSearchParams(window.location.search);
    params.delete('tab');
    window.history.pushState(null, '', `${categoryPath}${params.size ? '?' + params.toString() : ''}`);
  }

  return (
    <div className="bl-page rl-page">
      <a href="#main-content" className="bl-sr-only">Skip to main content</a>
      {isFlow
        ? <FlowNavBar />
        : isComet
        ? <CometNavBar />
        : isGalaxy
        ? <GalaxyNavBar />
        : isGravity
        ? <GravityNavBar />
        : isStoryhealth
        ? <StoryhealthNavBar />
        : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />}
        <main id="main-content">
        {/* Page header — always visible */}
        <section className="section-x-pad" style={{
          background: 'var(--seasons-color-surface-default)',
          paddingTop: 'var(--seasons-space-12)',
          paddingBottom: 'var(--seasons-space-8)',
        }}>
          <motion.div className="bl-page-header" initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp} className="type-display-xl"
              style={{ color: 'var(--seasons-color-prose-heading)', marginBottom: 0, maxWidth: 'none' }}>
              Resources
            </motion.h1>
            <motion.p variants={fadeUp} className="type-body-lg bl-page-header__sub">
              White papers, case studies, industry reports, and brochures from Innovaccer.
            </motion.p>
            {/* Search bar — always mounted */}
            <motion.div variants={fadeUp} className="webinars-search webinars-search--hero">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="webinars-search__icon">
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                className="webinars-search__input"
                placeholder="Search resources"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search resources"
              />
              {searchQuery && (
                <button className="webinars-search__clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                    <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </motion.div>
          </motion.div>
        </section>

        {/* Filter bar — always visible */}
        <div className="bl-filter-bar section-x-pad" style={{ paddingTop: 0, paddingBottom: 0, overflow: 'visible' }}>
          <div className="bl-fb-bar">
            <div role="group" aria-label="Filter by resource type" className="bl-fb-v2 bl-fb-v2--bare">
              {visibleTabs.map(tab => (
                <button
                  key={tab}
                  type="button"
                  className={`bl-fb-v2__tab${activeTab === tab ? ' bl-fb-v2__tab--active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                  aria-pressed={activeTab === tab}
                >
                  {TAB_DISPLAY[tab]}
                </button>
              ))}
            </div>
            <div className="bl-fb-controls">
              {!isBrand && <BrandDropdown selected={filterBrand} onChange={v => { setFilterBrand(v); setVisibleCount(PAGE_SIZE); }} />}
              {filterTags.length > 0 && (
                <FilterDropdown
                  tags={filterTags}
                  selected={selectedTags}
                  onChange={v => { setSelectedTags(v); setVisibleCount(PAGE_SIZE); }}
                />
              )}
              <SortDropdown value={sortOrder} onChange={v => { setSortOrder(v); setVisibleCount(PAGE_SIZE); }} />
            </div>
          </div>
        </div>

        {/* Grid — always visible, same card layout for browse and search */}
        <section className="section-x-pad" style={{
          background: 'var(--seasons-color-surface-subtle)',
          paddingTop: 'var(--seasons-space-12)',
          paddingBottom: 'var(--seasons-space-20)',
        }}>
          {searchQuery.trim() && (
            <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', marginBottom: 'var(--seasons-space-6)' }}>
              {filtered.length === 0
                ? `No results for "${searchQuery.trim()}"`
                : `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${searchQuery.trim()}"`}
            </p>
          )}
          {filtered.length === 0 ? (
            <div className="bl-no-results">
              <p className="type-heading-md" style={{ color: 'var(--seasons-color-prose-heading)', margin: 0 }}>
                {searchQuery.trim() ? 'No resources match your search' : `No ${activeTab === 'All' ? 'resources' : activeTab.toLowerCase()} yet`}
              </p>
              <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', margin: 0 }}>
                {searchQuery.trim() ? 'Try different keywords or clear the search.' : 'Check back soon — more coming.'}
              </p>
            </div>
          ) : (
            <>
              <motion.div className="bl-grid" initial="hidden" animate="visible" variants={stagger}>
                {filtered.slice(0, visibleCount).map(post => (
                  <ResourceCard key={post.slug} post={post} />
                ))}
              </motion.div>
              {filtered.length > visibleCount && (
                <div className="bl-load-more">
                  <p className="bl-load-count" aria-live="polite" aria-atomic="true">
                    Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} resources
                  </p>
                  <SimpleButton label="Load more" size="lg" variant="outline"
                    onClick={() => setVisibleCount(c => c + PAGE_SIZE)} textSwap />
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {isFlow ? <FlowFooter /> : isComet ? <CometFooter /> : isGalaxy ? <GalaxyFooter /> : isGravity ? <GravityFooter /> : isStoryhealth ? <StoryhealthFooter /> : <Footer theme="light" />}
    </div>
  );
}
